<template>
  <ListComponent :title="pageTitle" :totalCount="totalItems">
    <template #actions>
      <v-btn
        color="error"
        outlined
        small
        class="mr-2"
        :disabled="!selectedRows.length"
        @click="showBulkCancelDialog = true"
      >
        <v-icon left small>mdi-close-circle-outline</v-icon>
        Cancel{{ selectedRows.length ? ` (${selectedRows.length})` : "" }}
      </v-btn>
      <v-btn
        v-if="!isNonBondedCategory"
        color="primary"
        depressed
        small
        @click="showBulkUpdateDialog = true"
      >
        <v-icon left small>mdi-playlist-edit</v-icon>
        Bulk Update
      </v-btn>
    </template>
    <template #list>
      <v-alert
        v-if="categoryInfo"
        dense
        text
        type="info"
        icon="mdi-information-outline"
        class="mx-0 mb-3"
      >
        {{ categoryInfo }}
      </v-alert>
      <v-row class="ma-0" dense>
        <v-col cols="6" md="3" class="pl-0 pt-0 pb-2">
          <v-text-field
            label="Search"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="search"
            @click:clear="onSearchClear"
            @keyup.enter="onSearch"
          />
        </v-col>
        <v-col cols="6" md="auto" style="min-width:130px" class="pt-0 pb-2">
          <MultiSelect
            label="Status"
            :items="statusList"
            itemText="text"
            itemValue="value"
            clearable
            dense
            hide-details="auto"
            v-model="filters.status"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" md="auto" style="min-width:130px" class="pt-0 pb-2">
          <MultiSelect
            label="Origin"
            :items="locationList"
            itemText="location_code"
            itemValue="id"
            clearable
            dense
            hide-details="auto"
            v-model="filters.origin"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" md="auto" style="min-width:130px" class="pt-0 pb-2">
          <MultiSelect
            label="Destination"
            :items="locationList"
            itemText="location_code"
            itemValue="id"
            clearable
            dense
            hide-details="auto"
            v-model="filters.destination"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" md="auto" style="min-width:150px" class="pt-0 pb-2">
          <DateField
            :inputProps="{
              dense: true,
              label: 'Movement Date From',
              'hide-details': 'auto',
              clearable: true,
            }"
            v-model="filters.movement_date_from"
            @input="onFilterChange"
          />
        </v-col>
        <v-col cols="6" md="auto" style="min-width:150px" class="pt-0 pb-2">
          <DateField
            :inputProps="{
              dense: true,
              label: 'Movement Date To',
              'hide-details': 'auto',
              clearable: true,
            }"
            v-model="filters.movement_date_to"
            @input="onFilterChange"
          />
        </v-col>
        <v-col cols="12" md="auto" class="pr-0 pt-0 pb-2 d-flex align-center ml-md-auto justify-end">
          <Pagination
            v-if="totalItems > 0"
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
        <v-col cols="12" class="pa-0">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 190px)"
            class="ag-theme-alpine"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            rowSelection="multiple"
            :isRowSelectable="isRowSelectable"
            @selection-changed="onSelectionChanged"
            :rowData="allList"
          />
        </v-col>
      </v-row>
    </template>

    <template #dialogs>
      <v-dialog max-width="440px" v-model="showBulkCancelDialog" persistent>
        <v-card>
          <v-card-title class="secondary white--text py-3 px-5">
            <span class="text-subtitle-1 font-weight-bold">
              Cancel {{ selectedRows.length }} Draft DA{{ selectedRows.length > 1 ? "s" : "" }}
            </span>
            <v-spacer />
            <v-btn icon color="white" small @click="showBulkCancelDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="px-5 pt-4">
            <div class="caption grey--text mb-3">
              Only rows with status "Open" can be cancelled - any already Confirmed/Cancelled selections will be skipped.
            </div>
            <v-textarea
              label="Remarks (optional)"
              outlined
              dense
              hide-details="auto"
              rows="3"
              v-model="bulkCancelRemarks"
            />
          </v-card-text>
          <v-divider class="mt-3" />
          <v-card-actions class="px-5 py-3">
            <v-spacer />
            <v-btn depressed outlined @click="showBulkCancelDialog = false" class="mr-2">Back</v-btn>
            <v-btn color="error" depressed :loading="bulkCancelling" min-width="120" @click="confirmBulkCancel">
              <v-icon small class="mr-1">mdi-close-circle-outline</v-icon>
              Cancel Selected
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <DraftDABulkUpdateDialog v-model="showBulkUpdateDialog" @updated="onBulkUpdated" />
    </template>
  </ListComponent>
</template>
<script>
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import Pagination from "@/components/General/Pagination.vue";
import DraftDABulkUpdateDialog from "@/components/ModuleBased/dialogForms/DraftDABulkUpdateDialog.vue";
import DraftDAViewButton from "@/components/AgGridComponents/DraftDAViewButton.vue";
import DraftDAStatusChip from "@/components/AgGridComponents/DraftDAStatusChip.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    agColumnHeader: CustomHeader,
    AgGridVue,
    Pagination,
    DraftDAViewButton,
    DraftDAStatusChip,
    MultiSelect,
    DateField,
    DraftDABulkUpdateDialog,
  },
  data() {
    return {
      allList: [],
      totalItems: 0,
      search: "",
      filters: {
        status: null,
        origin: null,
        destination: null,
        movement_date_from: null,
        movement_date_to: null,
      },
      statusList: [
        { text: "Open", value: "open" },
        { text: "Confirmed", value: "confirmed" },
        { text: "Cancelled", value: "cancelled" },
      ],
      locationList: [],
      pageNo: 1,
      itemsPerPage: 50,
      permissions: null,
      selectedRows: [],
      showBulkCancelDialog: false,
      bulkCancelRemarks: "",
      bulkCancelling: false,
      showBulkUpdateDialog: false,
      defaultColDef: { resizable: true },
      gridOptions: {
        onGridSizeChanged: () => this.gridOptions.api.sizeColumnsToFit(),
        suppressRowClickSelection: true,
        enableCellTextSelection: true,
        rowHeight: 35,
      },
      headers: [
        {
          headerName: "",
          field: "_select",
          checkboxSelection: (params) => params.data && params.data.status === "open",
          minWidth: 44,
          maxWidth: 44,
          suppressSizeToFit: true,
          sortable: false,
          resizable: false,
        },
        {
          headerName: "Draft DA No",
          field: "dda_no",
          sortable: true,
          minWidth: 150,
          cellClass: ["primary--text font-weight-bold cursor-pointer"],
          onCellClicked: (params) => this.openDraftDADetails(params.data),
        },
        {
          headerName: "Status",
          field: "status",
          sortable: true,
          minWidth: 120,
          cellRendererFramework: "DraftDAStatusChip",
        },
        {
          headerName: "Event",
          field: "event_type",
          sortable: true,
          minWidth: 110,
          valueFormatter: (params) =>
            params.value === "gate_in" ? "Gate In" : "Gate Out",
        },
        {
          headerName: "Container No",
          field: "container_number",
          sortable: true,
          minWidth: 160,
        },
        {
          headerName: "VBS Ref No",
          field: "vbs_reference_no",
          sortable: true,
          minWidth: 150,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Container Type",
          field: "container_type_name",
          sortable: true,
          minWidth: 140,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Origin",
          field: "origin_name",
          sortable: true,
          minWidth: 160,
          // Same fallback as the detail page: unresolved FK -> raw CODECO value, marked "(unresolved)".
          valueGetter: (params) =>
            params.data
              ? params.data.origin_name || (params.data.origin_raw ? `${params.data.origin_raw} (unresolved)` : null)
              : null,
          valueFormatter: (params) => params.value || "-",
          cellStyle: (params) =>
            params.data && !params.data.origin_name && params.data.origin_raw ? { color: "#b45309" } : null,
        },
        {
          headerName: "Origin Code",
          field: "origin_code",
          sortable: true,
          minWidth: 120,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Destination",
          field: "destination_name",
          sortable: true,
          minWidth: 160,
          valueGetter: (params) =>
            params.data
              ? params.data.destination_name ||
                (params.data.destination_raw ? `${params.data.destination_raw} (unresolved)` : null)
              : null,
          valueFormatter: (params) => params.value || "-",
          cellStyle: (params) =>
            params.data && !params.data.destination_name && params.data.destination_raw
              ? { color: "#b45309" }
              : null,
        },
        {
          headerName: "Destination Code",
          field: "destination_code",
          sortable: true,
          minWidth: 130,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Vendor",
          field: "vendor_name",
          sortable: true,
          minWidth: 170,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Sub Hire",
          field: "is_subhire",
          sortable: true,
          minWidth: 100,
          valueFormatter: (params) => (params.value ? "Yes" : "No"),
        },
        {
          headerName: "Movement Date",
          field: "movement_date",
          sortable: true,
          minWidth: 140,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value) : "-",
        },
        {
          headerName: "DA No",
          field: "da_no",
          sortable: true,
          minWidth: 140,
          valueFormatter: (params) => params.value || "-",
          cellClass: (params) =>
            params.data && params.data.da
              ? "primary--text font-weight-bold cursor-pointer"
              : "",
          onCellClicked: (params) => this.openDaDetails(params.data),
        },
        {
          headerName: "Confirmed On",
          field: "confirmed_on",
          sortable: true,
          minWidth: 170,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value, true) : "-",
        },
        {
          headerName: "Created",
          field: "created",
          sortable: true,
          minWidth: 160,
        },
        {
          headerName: "Failure Reason",
          field: "failure_reason",
          sortable: false,
          minWidth: 280,
          tooltipValueGetter: (params) =>
            params.value ? params.value.split("\n").join(", ") : "",
          valueFormatter: (params) =>
            params.value ? params.value.split("\n").join(", ") : "-",
          cellClass: (params) => (params.value ? "red--text" : ""),
        },
        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "DraftDAViewButton",
          pinned: "right",
          minWidth: 90,
          width: 90,
          suppressSizeToFit: true,
        },
      ],
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
    pageTitle() {
      const titles = {
        ml_bonded: "Draft DAs - ML Bonded",
        subhire_bonded: "Draft DAs - Subhire Bonded",
        subhire_non_bonded: "Draft DAs - Subhire Non-Bonded",
        ml_non_bonded: "Draft DAs - ML Non-Bonded",
      };
      return titles[this.$route.query.category] || "Draft DAs";
    },
    categoryInfo() {
      const messages = {
        ml_bonded:
          "ML Bonded: Driver or Vehicle matches Momentum's own master data (Driver/Vehicle present in the system), " +
          "and the route is a bonded move (both Origin and Destination are bonded codes: KCT, SCT, SIC, SICD).",
        subhire_bonded:
          "Subhire Bonded: Driver and Vehicle do NOT match Momentum's master data (neither is recognised - a " +
          "vendor's own driver/vehicle), and the route is a bonded move (both Origin and Destination are bonded " +
          "codes: KCT, SCT, SIC, SICD).",
        subhire_non_bonded:
          "Subhire Non-Bonded: Driver and Vehicle do NOT match Momentum's master data, and the route is NOT a " +
          "bonded move (Origin and/or Destination falls outside KCT, SCT, SIC, SICD).",
        ml_non_bonded:
          "ML Non-Bonded: Driver or Vehicle matches Momentum's own master data, but the route is NOT a bonded " +
          "move (Origin and/or Destination falls outside KCT, SCT, SIC, SICD).",
      };
      return messages[this.$route.query.category] || null;
    },
    isNonBondedCategory() {
      return ["ml_non_bonded", "subhire_non_bonded"].includes(this.$route.query.category);
    },
  },
  watch: {
    "$route.query.category"() {
      this.pageNo = 1;
      this.getList();
    },
  },
  methods: {
    openDraftDADetails(row) {
      if (row && row.id) {
        this.$router.push({
          path: `/app/admin/draft-da/details/${row.id}`,
          query: {},
        });
      }
    },
    openDaDetails(row) {
      if (row && row.da) {
        this.$router.push({
          path: `/app/admin/delivery-advice/details/${row.da}`,
          query: {},
        });
      }
    },
    isRowSelectable(params) {
      return !!params.data && params.data.status === "open";
    },
    onSelectionChanged() {
      this.selectedRows = this.gridApi.getSelectedRows();
    },
    confirmBulkCancel() {
      this.bulkCancelling = true;
      this.$api.draftDA
        .bulkCancelDraftDA({
          ids: this.selectedRows.map((row) => row.id),
          remarks: this.bulkCancelRemarks,
        })
        .then((res) => {
          const data = res.data || res;
          const cancelledCount = (data.cancelled || []).length;
          const skippedCount = (data.skipped || []).length;
          this.$bus.$emit("showToastMessage", {
            message: skippedCount
              ? `${cancelledCount} cancelled, ${skippedCount} skipped (already resolved).`
              : `${cancelledCount} Draft DA${cancelledCount > 1 ? "s" : ""} cancelled.`,
            color: "success",
          });
          this.showBulkCancelDialog = false;
          this.bulkCancelRemarks = "";
          this.selectedRows = [];
          this.gridApi.deselectAll();
          this.getList();
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", {
            message: err?.message || "Failed to cancel selected Draft DAs.",
            color: "error",
          });
        })
        .finally(() => {
          this.bulkCancelling = false;
        });
    },
    onBulkUpdated() {
      this.selectedRows = [];
      if (this.gridApi) {
        this.gridApi.deselectAll();
      }
      this.getList();
    },
    onSearch() {
      this.pageNo = 1;
      this.getList();
    },
    onSearchClear() {
      this.search = "";
      this.pageNo = 1;
      this.getList();
    },
    onFilterChange() {
      this.pageNo = 1;
      this.getList();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getList();
    },
    prevPage() {
      this.pageNo--;
      this.getList();
    },
    nextPage() {
      this.pageNo++;
      this.getList();
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
    },
    getLocationList() {
      this.$api.location
        .getLocationSelectList()
        .then((res) => {
          this.locationList = res.data || res;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    getList() {
      this.$bus.$emit("showLoader", true);
      const params = {
        offset: this.offset,
        limit: this.itemsPerPage,
      };
      if (this.search) {
        params.search = this.search;
      }
      if (this.filters.status) {
        params.status = this.filters.status;
      }
      if (this.filters.origin) {
        params.origin = this.filters.origin;
      }
      if (this.filters.destination) {
        params.destination = this.filters.destination;
      }
      if (this.filters.movement_date_from) {
        params.movement_date_from = this.filters.movement_date_from;
      }
      if (this.filters.movement_date_to) {
        params.movement_date_to = this.filters.movement_date_to;
      }
      if (this.$route.query.category) {
        params.category = this.$route.query.category;
      }
      this.$api.draftDA
        .getDraftDAList(params)
        .then((res) => {
          this.allList = res.data;
          this.totalItems = res.count != null ? res.count : res.data.length;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("emergency_order_container");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this module!",
          color: "error",
        });
        this.$router.push(
          `/${localStorage.getItem("user_type").toLowerCase()}`,
        );
      } else {
        this.permissions = { ...permissions };
        this.getLocationList();
        this.getList();
      }
    }
  },
};
</script>
